import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Mail,
  Smartphone,
  MessageCircle,
  Eye,
  Heart,
  UserPlus,
  CreditCard,
  Moon,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { useNotifications } from '@/contexts/NotificationContext';
import { notificationService } from '@/lib/api/notification-service';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '@/layouts';
import { VStack, HStack } from '@/components/core';
import { Grid } from '@/components/core';
import { Skeleton } from '@/components/ui/skeleton';

interface NotificationPreferences {
  // Email notification flags (backend-supported)
  email_messages: boolean;
  email_profile_visits: boolean;
  email_matches: boolean;
  email_likes: boolean;
  email_follow_ups: boolean;
  email_system: boolean;
  email_purchases: boolean;
  
  // Push notification flags (backend-supported)
  push_messages: boolean;
  push_profile_visits: boolean;
  push_matches: boolean;
  push_likes: boolean;
  push_follow_ups: boolean;
  push_system: boolean;
  push_purchases: boolean;
  
  // Batching and quiet hours
  email_batch_enabled: boolean;
  email_batch_interval_minutes: number;
  quiet_hours_enabled: boolean;
  quiet_hours: {
    start: string;
    end: string;
    timezone?: string;
  };
}

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isPushEnabled, requestPushPermission } = useNotifications();
  const { t } = useTranslation(['notifications', 'common']);
  
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    // Email flags (follow_ups entfernt - wird als MESSAGE behandelt)
    email_messages: true,
    email_profile_visits: true,
    email_matches: true,
    email_likes: true,
    email_follow_ups: true, // Backend-Kompatibilitaet, wird nicht mehr genutzt
    email_system: true,
    email_purchases: true,
    // Push flags (follow_ups entfernt - wird als MESSAGE behandelt)
    push_messages: true,
    push_profile_visits: true,
    push_matches: true,
    push_likes: true,
    push_follow_ups: true, // Backend-Kompatibilitaet, wird nicht mehr genutzt
    push_system: true,
    push_purchases: true,
    // Batching & quiet hours
    email_batch_enabled: true,
    email_batch_interval_minutes: 60,
    quiet_hours_enabled: false,
    quiet_hours: {
      start: '22:00',
      end: '08:00'
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    void loadPreferences();
  }, []);
  
  
  const loadPreferences = async () => {
    try {
      const prefs = await notificationService.getPreferences();
      // quiet_hours kann null sein -> Standard setzen
      setPreferences(prev => ({
        ...prev,
        ...prefs,
        quiet_hours: prefs.quiet_hours ?? prev.quiet_hours
      }));
    } catch (error: unknown) {
      console.error('Failed to load preferences:', error);
      toast({
        title: t('common:errors.error'),
        description: t('settings.loadError'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const savePreferences = async () => {
    setSaving(true);
    try {
      // Automatisch Browser-Zeitzone zu quiet_hours hinzufuegen
      const prefsToSave = {
        ...preferences,
        quiet_hours: preferences.quiet_hours_enabled ? {
          ...preferences.quiet_hours,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        } : preferences.quiet_hours
      };
      
      await notificationService.updatePreferences(prefsToSave);
      toast({
        title: t('settings.saved'),
        description: t('settings.savedDescription'),
      });
    } catch (error: unknown) {
      console.error('Failed to save preferences:', error);
      toast({
        title: t('common:errors.error'),
        description: t('settings.saveError'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const toggleAllEmail = (enabled: boolean) => {
    setPreferences(prev => ({
      ...prev,
      email_messages: enabled,
      email_profile_visits: enabled,
      email_matches: enabled,
      email_likes: enabled,
      email_follow_ups: enabled, // Sync mit messages fuer Backend-Kompatibilitaet
      email_system: enabled,
      email_purchases: enabled,
    }));
  };
  
  const toggleAllPush = (enabled: boolean) => {
    setPreferences(prev => ({
      ...prev,
      push_messages: enabled,
      push_profile_visits: enabled,
      push_matches: enabled,
      push_likes: enabled,
      push_follow_ups: enabled, // Sync mit messages fuer Backend-Kompatibilitaet
      push_system: enabled,
      push_purchases: enabled,
    }));
  };
  
  // Hinweis: follow_ups wurde entfernt - Follow-Up Nachrichten werden als MESSAGE behandelt
  // und nutzen daher die 'messages' Einstellung
  const notificationTypes = [
    { key: 'messages', label: t('preferences.types.messages'), icon: MessageCircle },
    { key: 'profile_visits', label: t('preferences.types.profile_visits'), icon: Eye },
    { key: 'matches', label: t('preferences.types.matches'), icon: UserPlus },
    { key: 'likes', label: t('preferences.types.likes'), icon: Heart },
    { key: 'system', label: t('preferences.types.system'), icon: Bell },
    { key: 'purchases', label: t('types.coin_purchase.title'), icon: CreditCard },
  ] as const;
  
  if (loading) {
    return (
      <PageLayout maxWidth="xl" padding="md">
        <VStack gap="lg" className="first-content">
          {/* Header Skeleton */}
          <HStack gap="md" align="center">
            <Skeleton className="h-10 w-10 rounded-token-sm bg-white/10" />
            <Skeleton className="h-8 w-48 bg-white/10" />
          </HStack>

          {/* Push Permission Card Skeleton */}
          <Card className="design-card">
            <CardHeader>
              <Skeleton className="h-6 w-48 bg-white/10" />
              <Skeleton className="h-4 w-full mt-token-sm bg-white/10" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-48 bg-white/10" />
            </CardContent>
          </Card>

          {/* Email Notifications Card Skeleton */}
          <Card className="design-card">
            <CardHeader>
              <HStack justify="between" align="start">
                <VStack gap="xs">
                  <Skeleton className="h-6 w-40 bg-white/10" />
                  <Skeleton className="h-4 w-64 bg-white/10" />
                </VStack>
                <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
              </HStack>
            </CardHeader>
            <CardContent>
              <VStack gap="md">
                {[...Array(6)].map((_, i) => (
                  <HStack key={i} justify="between" align="center">
                    <HStack gap="sm" align="center">
                      <Skeleton className="h-4 w-4 bg-white/10" />
                      <Skeleton className="h-4 w-32 bg-white/10" />
                    </HStack>
                    <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
                  </HStack>
                ))}
                <Skeleton className="h-px w-full my-token-sm bg-white/10" />
                <HStack justify="between" align="start">
                  <VStack gap="xs">
                    <Skeleton className="h-4 w-36 bg-white/10" />
                    <Skeleton className="h-4 w-56 bg-white/10" />
                  </VStack>
                  <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
                </HStack>
              </VStack>
            </CardContent>
          </Card>

          {/* Push Notifications Card Skeleton */}
          <Card className="design-card">
            <CardHeader>
              <HStack justify="between" align="start">
                <VStack gap="xs">
                  <Skeleton className="h-6 w-44 bg-white/10" />
                  <Skeleton className="h-4 w-60 bg-white/10" />
                </VStack>
                <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
              </HStack>
            </CardHeader>
            <CardContent>
              <VStack gap="md">
                {[...Array(6)].map((_, i) => (
                  <HStack key={i} justify="between" align="center">
                    <HStack gap="sm" align="center">
                      <Skeleton className="h-4 w-4 bg-white/10" />
                      <Skeleton className="h-4 w-32 bg-white/10" />
                    </HStack>
                    <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
                  </HStack>
                ))}
              </VStack>
            </CardContent>
          </Card>

          {/* Quiet Hours Card Skeleton */}
          <Card className="design-card">
            <CardHeader>
              <HStack justify="between" align="start">
                <VStack gap="xs">
                  <Skeleton className="h-6 w-32 bg-white/10" />
                  <Skeleton className="h-4 w-72 bg-white/10" />
                </VStack>
                <Skeleton className="h-6 w-11 rounded-full bg-white/10" />
              </HStack>
            </CardHeader>
            <CardContent>
              <Grid cols={2} gap="md">
                <VStack gap="xs">
                  <Skeleton className="h-4 w-16 bg-white/10" />
                  <Skeleton className="h-10 w-full bg-white/10" />
                </VStack>
                <VStack gap="xs">
                  <Skeleton className="h-4 w-16 bg-white/10" />
                  <Skeleton className="h-10 w-full bg-white/10" />
                </VStack>
              </Grid>
            </CardContent>
          </Card>

          {/* Save Button Skeleton */}
          <HStack justify="end">
            <Skeleton className="h-11 w-32 bg-white/10" />
          </HStack>
        </VStack>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="xl" padding="md">
      <VStack gap="lg" className="first-content">
        {/* Header */}
        <HStack gap="md" align="center">
          <Button
            variant="glass"
            size="icon"
            onClick={() => navigate(-1)}
            className="design-glass design-text-secondary hover:design-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold design-text-primary">{t('settings.title')}</h1>
        </HStack>

        {/* Push Notification Permission */}
        {!isPushEnabled && (
          <Card className="design-card">
            <CardHeader>
              <CardTitle>{t('settings.permission.title')}</CardTitle>
              <CardDescription>
                {t('settings.permission.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => { void requestPushPermission(); }}
                variant="gradient"
                className="w-full md:w-auto"
              >
                <Bell className="mr-token-sm h-4 w-4" />
                {t('settings.permission.button')}
              </Button>
              <p className="mt-token-sm text-xs text-muted-foreground md:hidden">
                {t('settings.permission.mobileHint', 'Auf iOS musst du diese App zum Home-Screen hinzufuegen, um Push-Nachrichten zu erhalten.')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Email Notifications */}
        <Card className="design-card">
          <CardHeader>
            {/* Desktop Layout */}
            <HStack justify="between" align="start" className="hidden md:flex">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Mail className="h-5 w-5" />
                  {t('settings.email.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.email.description')}
                </CardDescription>
              </VStack>
              <Switch
                checked={preferences.email_messages && preferences.email_profile_visits}
                onCheckedChange={toggleAllEmail}
              />
            </HStack>
            {/* Mobile Layout */}
            <VStack gap="sm" className="md:hidden">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Mail className="h-5 w-5" />
                  {t('settings.email.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.email.description')}
                </CardDescription>
              </VStack>
              <HStack align="center" justify="between">
                <span className="text-sm text-muted-foreground">{t('settings.email.enableAll', 'Alle aktivieren')}</span>
                <Switch
                  checked={preferences.email_messages && preferences.email_profile_visits}
                  onCheckedChange={toggleAllEmail}
                />
              </HStack>
            </VStack>
          </CardHeader>
          <CardContent>
            <VStack gap="md">
              {notificationTypes.map(({ key, label, icon: Icon }) => (
                <HStack key={key} justify="between" align="center">
                  <Label htmlFor={`email-${key}`} className="flex items-center gap-token-sm cursor-pointer">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {label}
                  </Label>
                  <Switch
                    id={`email-${key}`}
                    checked={preferences[`email_${key}` as keyof NotificationPreferences] as boolean}
                    onCheckedChange={(checked) =>
                      setPreferences(prev => ({ ...prev, [`email_${key}`]: checked }))
                    }
                  />
                </HStack>
              ))}

              <Separator className="my-token-sm" />

              <HStack justify="between" align="start">
                <VStack gap="xs">
                  <Label htmlFor="email-batch">{t('settings.email.batch')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.email.batchDescription')}
                  </p>
                </VStack>
                <Switch
                  id="email-batch"
                  checked={preferences.email_batch_enabled}
                  onCheckedChange={(checked) =>
                    setPreferences(prev => ({ ...prev, email_batch_enabled: checked }))
                  }
                />
              </HStack>
            </VStack>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card className="design-card">
          <CardHeader>
            {/* Desktop Layout */}
            <HStack justify="between" align="start" className="hidden md:flex">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Smartphone className="h-5 w-5" />
                  {t('settings.push.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.push.description')}
                </CardDescription>
              </VStack>
              <Switch
                checked={preferences.push_messages && preferences.push_profile_visits}
                onCheckedChange={toggleAllPush}
                disabled={!isPushEnabled}
              />
            </HStack>
            {/* Mobile Layout */}
            <VStack gap="sm" className="md:hidden">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Smartphone className="h-5 w-5" />
                  {t('settings.push.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.push.description')}
                </CardDescription>
              </VStack>
              <HStack align="center" justify="between">
                <span className="text-sm text-muted-foreground">{t('settings.push.enableAll', 'Alle aktivieren')}</span>
                <Switch
                  checked={preferences.push_messages && preferences.push_profile_visits}
                  onCheckedChange={toggleAllPush}
                  disabled={!isPushEnabled}
                />
              </HStack>
            </VStack>
          </CardHeader>
          <CardContent>
            <VStack gap="md">
              {notificationTypes.map(({ key, label, icon: Icon }) => (
                <HStack key={key} justify="between" align="center">
                  <Label
                    htmlFor={`push-${key}`}
                    className={cn(
                      "flex items-center gap-token-sm cursor-pointer",
                      !isPushEnabled && "opacity-50"
                    )}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {label}
                  </Label>
                  <Switch
                    id={`push-${key}`}
                    checked={preferences[`push_${key}` as keyof NotificationPreferences] as boolean}
                    onCheckedChange={(checked) =>
                      setPreferences(prev => ({ ...prev, [`push_${key}`]: checked }))
                    }
                    disabled={!isPushEnabled}
                  />
                </HStack>
              ))}
            </VStack>
          </CardContent>
        </Card>

        {/* Quiet Hours */}
        <Card className="design-card">
          <CardHeader>
            {/* Desktop Layout */}
            <HStack justify="between" align="start" className="hidden md:flex">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Moon className="h-5 w-5" />
                  {t('settings.quietHours.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.quietHours.description')}
                </CardDescription>
              </VStack>
              <Switch
                checked={preferences.quiet_hours_enabled}
                onCheckedChange={(checked) =>
                  setPreferences(prev => ({ ...prev, quiet_hours_enabled: checked }))
                }
              />
            </HStack>
            {/* Mobile Layout */}
            <VStack gap="sm" className="md:hidden">
              <VStack gap="xs">
                <CardTitle className="flex items-center gap-token-sm">
                  <Moon className="h-5 w-5" />
                  {t('settings.quietHours.title')}
                </CardTitle>
                <CardDescription>
                  {t('settings.quietHours.description')}
                </CardDescription>
              </VStack>
              <HStack align="center" justify="between">
                <span className="text-sm text-muted-foreground">{t('settings.quietHours.enable', 'Aktivieren')}</span>
                <Switch
                  checked={preferences.quiet_hours_enabled}
                  onCheckedChange={(checked) =>
                    setPreferences(prev => ({ ...prev, quiet_hours_enabled: checked }))
                  }
                />
              </HStack>
            </VStack>
          </CardHeader>
          {preferences.quiet_hours_enabled && (
            <CardContent>
              <Grid cols={2} gap="md">
                <VStack gap="xs">
                  <Label htmlFor="quiet-start">{t('settings.quietHours.start')}</Label>
                  <Input
                    id="quiet-start"
                    type="time"
                    value={preferences.quiet_hours.start}
                    onChange={(e) =>
                      setPreferences(prev => ({
                        ...prev,
                        quiet_hours: { ...prev.quiet_hours, start: e.target.value }
                      }))
                    }
                  />
                </VStack>
                <VStack gap="xs">
                  <Label htmlFor="quiet-end">{t('settings.quietHours.end')}</Label>
                  <Input
                    id="quiet-end"
                    type="time"
                    value={preferences.quiet_hours.end}
                    onChange={(e) =>
                      setPreferences(prev => ({
                        ...prev,
                        quiet_hours: { ...prev.quiet_hours, end: e.target.value }
                      }))
                    }
                  />
                </VStack>
              </Grid>
            </CardContent>
          )}
        </Card>

        {/* Save Button */}
        <HStack justify="end">
          <Button
            onClick={() => { void savePreferences(); }}
            disabled={saving}
            variant="gradient"
            size="lg"
            className="shadow-token-modal"
          >
            <Save className="mr-token-sm h-4 w-4" />
            {saving ? t('settings.saving') : t('settings.save')}
          </Button>
        </HStack>
      </VStack>
    </PageLayout>
  );
};

export default NotificationSettings;